import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_DEPARTURE_TYPES,
  normalizeDepartureType,
  calculateTerminationEOSB,
  getNoticePeriodDays,
  getDefaultSaudiDepartureFields,
  LEGACY_DEPARTURE_TYPE_MAP,
} from './utils/hrSaudiFormat';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserMinus, Search, Plus, Edit, Trash2, RefreshCcw, AlertTriangle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, DollarSign, FileText, Clock, List, LayoutList, Check, Users, Briefcase, Home, ClipboardList, StickyNote } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { normalizeDepartureRecord } from '../../shared/utils/hrApiRecordNormalizers';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  buildHrEmployeeSelectOptions,
  buildHrEmployeePickerPatch,
  resolveHrEmployeeFromLabel,
  resolveHrEmployeeMatricule,
  useHrEmployees,
  sliceHrListItems,
} from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'departure_number', labelKey: 'col_departure_number' },
  { key: 'employee_name', labelKey: 'label_employee' },
  { key: 'departure_date', labelKey: 'col_departure_date' },
  { key: 'departure_type', labelKey: 'label_type' },
  { key: 'reason', labelKey: 'label_reason' },
  { key: 'final_settlement_amount', labelKey: 'col_settlement' },
  { key: 'status', labelKey: 'label_status' },
  { key: 'actions', required: true },
];

const NS = 'modules.ressources_humaines.pages.departsPage';

const DepartsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const { settings } = useSettings();
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const saudiDepartureDefaults = getDefaultSaudiDepartureFields();
  const hrConfig = useHrConfig();

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return null;
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('na');
    return formatBrandMoney(n);
  };

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('na'));
  const [searchTerm, setSearchTerm] = useState('');
  const [departures, setDepartures] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  
  // Live employee picker — refreshes on HR_EMPLOYEES_REFRESH_EVENT
  const { employees: realEmployees } = useHrEmployees();
  
  const [formTab, setFormTab] = useState('departure');
  
  const [formData, setFormData] = useState({ 
    departure_number: '',
    employee_id: '',
    employee_name: '',
    departure_date: '',
    notice_date: '',
    departure_type: '',
    departure_reason: '',
    status: 'en_cours',
    final_settlement_amount: '',
    settlement_date: '',
    exit_interview_conducted: false,
    exit_interview_notes: '',
    rehire_eligible: true,
    notes: '',
    basicSalaryForEosb: '',
    eosbAmount: '',
    noticePeriodDays: saudiDepartureDefaults.noticePeriodDays,
    exitVisaRequired: false,
    exitVisaStatus: '',
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_departures');
  const [quickFilter, setQuickFilter] = useState('all');
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  useEffect(() => { loadDepartures(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);      if (!event.target.closest('.relative')) {      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDepartures = async () => {
    try {
      const response = await localApiAdapter.get('/hr/departures');
      const raw = response?.data ?? response ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setDepartures(sortRecordsNewestFirst(list.map(normalizeDepartureRecord)));
    } catch (error) {
      console.error('Error loading departures:', error);
      setDepartures([]);
    }
  };

  const generateDepartureNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'departure', 'DEP');
    return generateEntityReference(prefix, departures, (d) => d.departure_number);
  }, [departures, settings]);

  const countByDepartureType = (typeKey) =>
    departures.filter((d) => normalizeDepartureType(d.departure_type) === typeKey).length;

  const stats = {
    total: departures.length,
    demissions: countByDepartureType(SAUDI_DEPARTURE_TYPES.RESIGNATION),
    licenciements: countByDepartureType(SAUDI_DEPARTURE_TYPES.TERMINATION),
    retraites: countByDepartureType(SAUDI_DEPARTURE_TYPES.RETIREMENT),
    en_cours: departures.filter(d => d.status === 'en_cours').length
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_departures', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_type', name: t('common.by_type'), icon: Layers },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield }
  ]), [t, p]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quick_all'), icon: Layers },
    { id: SAUDI_DEPARTURE_TYPES.RESIGNATION, name: p('quick_demissions'), icon: Home },
    { id: SAUDI_DEPARTURE_TYPES.TERMINATION, name: p('quick_licenciements'), icon: AlertTriangle },
    { id: 'en_cours', name: p('quick_en_cours'), icon: Clock }
  ]), [p]);

  const departuresDistribution = useMemo(() => ({
    all_departures: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'demission', name: p('dist_demission'), count: stats.demissions, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'licenciement', name: p('dist_licenciement'), count: stats.licenciements, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'retraite', name: p('dist_retraite'), count: stats.retraites, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'fin_contrat', name: p('dist_fin_contrat'), count: countByDepartureType(SAUDI_DEPARTURE_TYPES.CONTRACT_END), color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'demission', name: p('dist_demission'), count: stats.demissions, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'licenciement', name: p('dist_licenciement'), count: stats.licenciements, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'retraite', name: p('dist_retraite'), count: stats.retraites, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'fin_contrat', name: p('dist_fin_contrat'), count: countByDepartureType(SAUDI_DEPARTURE_TYPES.CONTRACT_END), color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'mutation', name: p('dist_mutation'), count: countByDepartureType(SAUDI_DEPARTURE_TYPES.MUTUAL_AGREEMENT), color: 'bg-gradient-to-r from-green-500 to-green-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { filterKey: 'en_cours', name: p('dist_en_cours'), count: stats.en_cours, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'valide', name: p('dist_valide'), count: departures.filter(d => d.status === 'validé').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'annule', name: p('dist_annule'), count: departures.filter(d => d.status === 'annulé').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' }
      ]
    }
  }), [p, stats, departures]);

  const distributionInfo = departuresDistribution[mainViewMode] || departuresDistribution.all_departures;

  const baseFilteredDepartures = departures.filter(d => {
    // Search filter
    if (searchTerm && !d.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !d.departure_number?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_departures') {
      if (quickFilter === SAUDI_DEPARTURE_TYPES.RESIGNATION && normalizeDepartureType(d.departure_type) !== SAUDI_DEPARTURE_TYPES.RESIGNATION) return false;
      if (quickFilter === SAUDI_DEPARTURE_TYPES.TERMINATION && normalizeDepartureType(d.departure_type) !== SAUDI_DEPARTURE_TYPES.TERMINATION) return false;
      if (quickFilter === 'en_cours' && d.status !== 'en_cours') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_departures':
        case 'par_type':
          if (activeDistributionFilter === 'demission' && normalizeDepartureType(d.departure_type) !== SAUDI_DEPARTURE_TYPES.RESIGNATION) return false;
          if (activeDistributionFilter === 'licenciement' && normalizeDepartureType(d.departure_type) !== SAUDI_DEPARTURE_TYPES.TERMINATION) return false;
          if (activeDistributionFilter === 'retraite' && normalizeDepartureType(d.departure_type) !== SAUDI_DEPARTURE_TYPES.RETIREMENT) return false;
          if (activeDistributionFilter === 'fin_contrat' && normalizeDepartureType(d.departure_type) !== SAUDI_DEPARTURE_TYPES.CONTRACT_END) return false;
          if (activeDistributionFilter === 'mutation' && normalizeDepartureType(d.departure_type) !== SAUDI_DEPARTURE_TYPES.MUTUAL_AGREEMENT) return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'en_cours' && d.status !== 'en_cours') return false;
          if (activeDistributionFilter === 'valide' && d.status !== 'validé') return false;
          if (activeDistributionFilter === 'annule' && d.status !== 'annulé') return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('departure_number', (r) => r.departure_number || ''),
    textColumnRule('employee_name', (r) => r.employee_name || ''),
    textColumnRule('departure_date', (r) => (r.departure_date ? new Date(r.departure_date).toLocaleDateString(dateLocale) : '')),
    textColumnRule('departure_type', (r) => r.departure_type || ''),
    textColumnRule('final_settlement_amount', (r) => (r.final_settlement_amount != null ? formatBrandMoney(r.final_settlement_amount) : '')),
    exactColumnRule('status', (r) => r.status || ''),
  ], [dateLocale]);

  const filteredDepartures = useMemo(
    () => sliceHrListItems(
      applyColumnFilters(baseFilteredDepartures, colFilters, columnFilterRules),
      hrConfig.itemsPerPage,
    ),
    [baseFilteredDepartures, colFilters, columnFilterRules, hrConfig.itemsPerPage],
  );


  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredDepartures, selectedRecords, setSelectedRecords);
  const handleCalculateEosb = () => {
    const employee = resolveHrEmployeeFromLabel(realEmployees, formData.employee_name);
    const basic = parseFloat(formData.basicSalaryForEosb) || parseFloat(employee?.salaireBase) || 0;
    const startDate = employee?.dateEmbauche || employee?.startDate || '';
    const result = calculateTerminationEOSB({
      basicSalary: basic,
      startDate,
      endDate: formData.departure_date,
      departureType: formData.departure_type,
    });
    setFormData((prev) => ({
      ...prev,
      basicSalaryForEosb: basic || prev.basicSalaryForEosb,
      eosbAmount: result.payableEOSB,
      final_settlement_amount: String(result.payableEOSB),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = [
      { field: 'departure_number', label: p('label_departure_number') },
      { field: 'employee_name', label: p('col_employee_name') },
      { field: 'departure_date', label: p('label_departure_date') },
      { field: 'departure_type', label: p('label_departure_type') }
    ];
    
    const missingFields = requiredFields.filter(
      ({ field }) => !formData[field] || formData[field].trim() === ''
    );
    
    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(({ label }) => label).join(', ');
      alert(p('alert_required_fields', { fields: missingLabels }));
      return;
    }
    
    try {
      const departureNumber = (formData.departure_number || '').trim() || generateDepartureNumber();
      const submitData = { 
        departure_number: departureNumber,
        employee_id: formData.employee_id ? String(formData.employee_id) : null,
        employee_name: formData.employee_name,
        departure_date: formData.departure_date,
        notice_date: formData.notice_date || null,
        departure_type: formData.departure_type,
        departure_reason: formData.departure_reason,
        status: formData.status,
        final_settlement_amount: parseFloat(formData.final_settlement_amount) || 0,
        settlement_date: formData.settlement_date || null,
        exit_interview_conducted: formData.exit_interview_conducted ? 1 : 0,
        exit_interview_notes: formData.exit_interview_notes || '',
        rehire_eligible: formData.rehire_eligible ? 1 : 0,
        notes: formData.notes || '',
        basic_salary_for_eosb: parseFloat(formData.basicSalaryForEosb) || null,
        eosb_amount: parseFloat(formData.eosbAmount || formData.final_settlement_amount) || 0,
        notice_period_days: parseInt(formData.noticePeriodDays, 10) || getNoticePeriodDays(),
        exit_visa_required: formData.exitVisaRequired ? 1 : 0,
        exit_visa_status: formData.exitVisaStatus || '',
      };
      if (editingRecord) {
        await localApiAdapter.put(`/hr/departures/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'نهاية الخدمة' }));
      } else {
        await localApiAdapter.post('/hr/departures', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'نهاية الخدمة' }));
      }
      resetForm();
      await loadDepartures();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('na') }));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      departure_number: record.departure_number || '',
      employee_id: record.employee_id?.toString() || '',
      employee_name: record.employee_name || '',
      departure_date: record.departure_date || '',
      notice_date: record.notice_date || '',
      departure_type: normalizeDepartureType(record.departure_type) || record.departure_type || '',
      departure_reason: record.departure_reason || '',
      status: record.status || 'en_cours',
      final_settlement_amount: record.final_settlement_amount?.toString() || '',
      settlement_date: record.settlement_date || '',
      exit_interview_conducted: record.exit_interview_conducted === 1,
      exit_interview_notes: record.exit_interview_notes || '',
      rehire_eligible: record.rehire_eligible === 1,
      notes: record.notes || '',
      basicSalaryForEosb: record.basic_salary_for_eosb?.toString() || record.basicSalaryForEosb?.toString() || '',
      eosbAmount: record.eosb_amount?.toString() || record.eosbAmount?.toString() || '',
      noticePeriodDays: record.notice_period_days ?? getDefaultSaudiDepartureFields().noticePeriodDays,
      exitVisaRequired: record.exit_visa_required === 1 || record.exitVisaRequired === true,
      exitVisaStatus: record.exit_visa_status || record.exitVisaStatus || '',
    });
    setFormTab('departure');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/hr/departures/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'نهاية الخدمة' }));
        loadDepartures();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف سجل نهاية الخدمة' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/hr/departures/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'سجل نهاية خدمة' }));
        setSelectedRecords([]);
        loadDepartures();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف السجلات المحددة' }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      departure_number: '',
      employee_id: '',
      employee_name: '',
      departure_date: '',
      notice_date: '',
      departure_type: '',
      departure_reason: '',
      status: 'en_cours',
      final_settlement_amount: '',
      settlement_date: '',
      exit_interview_conducted: false,
      exit_interview_notes: '',
      rehire_eligible: true,
      notes: '',
      basicSalaryForEosb: '',
      eosbAmount: '',
      noticePeriodDays: getDefaultSaudiDepartureFields().noticePeriodDays,
      exitVisaRequired: false,
      exitVisaStatus: '',
    });
    setEditingRecord(null);
    setFormTab('departure');
    setShowModal(false);
  };

  const openNewDepartureModal = () => {
    setEditingRecord(null);
    setFormData({
      departure_number: generateDepartureNumber(),
      employee_id: '',
      employee_name: '',
      departure_date: '',
      notice_date: '',
      departure_type: '',
      departure_reason: '',
      status: 'en_cours',
      final_settlement_amount: '',
      settlement_date: '',
      exit_interview_conducted: false,
      exit_interview_notes: '',
      rehire_eligible: true,
      notes: '',
      basicSalaryForEosb: '',
      eosbAmount: '',
      noticePeriodDays: getDefaultSaudiDepartureFields().noticePeriodDays,
      exitVisaRequired: false,
      exitVisaStatus: '',
    });
    setFormTab('departure');
    setShowModal(true);
  };

  // ============================================
  // 📤 EXPORT FUNCTIONALITY
  // ============================================

  // Export to CSV (JSON format)
  const exportHeaders = useMemo(() => ([
    { key: 'departure_number', label: p('col_departure_number') },
    { key: 'employee_name', label: p('label_employee') },
    { key: 'departure_date', label: p('label_departure_date') },
    { key: 'notice_date', label: p('col_notice_date') },
    { key: 'departure_type', label: p('label_type') },
    { key: 'departure_reason', label: p('label_reason') },
    { key: 'status', label: p('label_status') },
    { key: 'final_settlement_amount', label: p('col_final_settlement') },
    { key: 'settlement_date', label: p('col_settlement_date') },
    { key: 'exit_interview_conducted', label: p('col_exit_interview') },
    { key: 'rehire_eligible', label: p('col_rehire') }
  ]), [p]);

  const handleExport = () => {
    const headers = exportHeaders;

    const dataToExport = filteredDepartures;
    const fileName = `departs_export_${new Date().toISOString().split('T')[0]}`;

    try {
      exportToCSV(dataToExport, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = filteredDepartures.map((item) => ({
        [p('label_employee')]: item.employee_name || p('na'),
        [p('label_departure_date')]: fmtDate(item.departure_date),
        [p('label_type')]: item.departure_type || p('na'),
        [p('label_status')]: item.status || p('na'),
        [p('col_settlement')]: formatAmountDisplay(item.final_settlement_amount || 0),
      }));
      await generateReportPDF(p('report_title'), reportData, { orientation: 'landscape' });
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = filteredDepartures.map((item) => ({
        [p('col_departure_number')]: item.departure_number || '',
        [p('label_employee')]: item.employee_name || '',
        [p('label_departure_date')]: item.departure_date ? new Date(item.departure_date).toLocaleDateString(dateLocale) : '',
        [p('col_notice_date')]: item.notice_date ? new Date(item.notice_date).toLocaleDateString(dateLocale) : '',
        [p('label_type')]: item.departure_type || '',
        [p('label_reason')]: item.departure_reason || '',
        [p('label_status')]: item.status || '',
        [p('col_final_settlement')]: formatAmountDisplay(parseFloat(item.final_settlement_amount || 0)),
        [p('col_settlement_date')]: item.settlement_date ? new Date(item.settlement_date).toLocaleDateString(dateLocale) : '',
        [p('col_exit_interview')]: item.exit_interview_conducted ? p('yes') : p('no'),
        [p('col_rehire')]: item.rehire_eligible ? p('yes') : p('no'),
      }));
      await generateReportExcel(p('report_title'), reportData);
    } catch (error) {
      console.error('Excel export error:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // ============================================
  // 📥 IMPORT FUNCTIONALITY
  // ============================================

  const handleImport = () => {
    const headers = exportHeaders.filter(h => h.key !== 'exit_interview_conducted' && h.key !== 'rehire_eligible');

    const schema = {
      requiredFields: ['departure_number', 'employee_name', 'departure_date', 'departure_type'],
      validators: {
        departure_type: (value) => Object.values(SAUDI_DEPARTURE_TYPES).includes(normalizeDepartureType(value)) || Object.keys(LEGACY_DEPARTURE_TYPE_MAP).includes(value),
        status: (value) => !value || ['en_cours', 'validé', 'annulé'].includes(value)
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/hr/departures', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          
          await loadDepartures();
          
          alert(
            p('alert_import_success', { total: result.totalCount, imported: successCount }) +
            (result.invalidCount > 0 ? '\n' + p('alert_import_errors', { count: result.invalidCount, details: result.errors.slice(0, 5).join('\n') }) : '')
          );
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };
  const statusOptions = useMemo(() => ([
    { value: 'en_cours', label: p('status_en_cours'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'validé', label: p('status_valide'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'annulé', label: p('status_annule'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
  ]), [p]);

  const getStatusLabel = useCallback((status) => {
    const labels = { en_cours: p('status_en_cours_short'), validé: p('status_valide'), annulé: p('status_annule') };
    return labels[status] || p('empty_dash');
  }, [p]);

  const statusFilterOptions = useMemo(() => ([
    { value: 'all', label: p('status_all'), dot: 'bg-white/50' },
    { value: 'en_cours', label: p('status_en_cours_short'), dot: 'bg-orange-400' },
    { value: 'validé', label: p('status_valide'), dot: 'bg-green-400' },
    { value: 'annulé', label: p('status_annule'), dot: 'bg-gray-400' }
  ]), [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/departures/${recordId}`, { status: newStatus });
      await loadDepartures();
      setViewingRecord((prev) =>
        prev && String(prev.id) === String(recordId) ? { ...prev, status: newStatus } : prev
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Simple non-clickable status badge
  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  // Clickable status badge component

  const qawaemStatusOptions = useMemo(
    () => [{ value: 'all', label: p('status_all') }, ...statusFilterOptions.filter((o) => o.value !== 'all')],
    [statusFilterOptions, p],
  );
  const listStats = useMemo(() => [
    { key: 'total', label: p('stat_total'), value: stats.total, icon: UserMinus, variant: 'copper' },
    { key: 'demissions', label: p('stat_demissions'), value: stats.demissions, icon: Home, variant: 'forest' },
    { key: 'licenciements', label: p('stat_licenciements'), value: stats.licenciements, icon: AlertTriangle, variant: 'copper' },
    { key: 'en_cours', label: p('stat_en_cours'), value: stats.en_cours, icon: Clock, variant: 'forest' },
  ], [stats, p]);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(
      HR_TABLE_COLUMNS.map((col) => (
        col.key === 'actions' ? { ...col, label: t('common.actions') } : col
      )),
      p,
      columnVisibility,
    ),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);
  const importExportProps = useMemo(() => buildHrImportExportProps({
    showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown,
    importDropdownRef, exportDropdownRef, p, t, isGeneratingPDF,
    handlers: { onImport: handleImport, onExportCSV: handleExport, onExportExcel: handleExportExcel, onExportPDF: handleExportPDF },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t]);
  const departureFormTabs = useMemo(() => ([
    { id: 'departure', label: p('section_departure'), icon: UserMinus, iconClassName: 'text-blue-400' },
    { id: 'settlement', label: p('section_settlement'), icon: DollarSign, iconClassName: 'text-amber-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-0">
<div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_departures' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.filterKey}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterKey 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_click_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
<h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('pct_of_departures', { pct: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
        </div>
      </motion.div>
    </div>
  );


  const getTypeBadge = useCallback((type) => {
    const normalized = normalizeDepartureType(type);
    const badges = {
      [SAUDI_DEPARTURE_TYPES.RESIGNATION]: { label: p('type_resignation'), style: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Home },
      [SAUDI_DEPARTURE_TYPES.TERMINATION]: { label: p('type_termination'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: AlertTriangle },
      [SAUDI_DEPARTURE_TYPES.RETIREMENT]: { label: p('type_retirement'), style: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Users },
      [SAUDI_DEPARTURE_TYPES.CONTRACT_END]: { label: p('type_contract_end'), style: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: FileText },
      [SAUDI_DEPARTURE_TYPES.MUTUAL_AGREEMENT]: { label: p('type_mutual_agreement'), style: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Briefcase },
    };
    const config = badges[normalized] || badges[SAUDI_DEPARTURE_TYPES.RESIGNATION];
    const Icon = config.icon;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.style}`}><Icon size={14} />{config.label}</span>;
  }, [p]);

    return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={UserMinus}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedRecords.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedRecords.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewDepartureModal}
        createLabel={p('new_item')}
        importExport={importExportProps}
        headerActions={<button type="button" onClick={loadDepartures} className="refresh-button" title={p('refresh_title')}><RefreshCcw size={20} /></button>}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) }),
          cardsTitle: t('common.view_cards', { defaultValue: 'بطاقات' }),
          tableTitle: t('common.view_table', { defaultValue: 'جدول' }),
        })}
      >
        {viewMode === 'table' && (
        <>
        <div className={facturationTableScrollClass(qawaem)}>
          <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox size="lg" checked={allRowsSelected} indeterminate={someRowsSelected} onChange={handleSelectAll} /></div></th>
                  
                  {isColVisible('departure_number') && (
                  <TableColumnHeaderFilter
                    columnKey="departure_number"
                    label={p('col_departure_number')}
                    value={colFilters.departure_number ?? ''}
                    onChange={(v) => setColFilter('departure_number', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('employee_name') && (
                  <TableColumnHeaderFilter
                    columnKey="employee_name"
                    label={p('label_employee')}
                    value={colFilters.employee_name ?? ''}
                    onChange={(v) => setColFilter('employee_name', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('departure_date') && (
                  <TableColumnHeaderFilter
                    columnKey="departure_date"
                    label={p('col_departure_date')}
                    value={colFilters.departure_date ?? ''}
                    onChange={(v) => setColFilter('departure_date', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('departure_type') && (
                  <TableColumnHeaderFilter
                    columnKey="departure_type"
                    label={p('label_type')}
                    value={colFilters.departure_type ?? ''}
                    onChange={(v) => setColFilter('departure_type', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('reason') && <th className="px-6 py-4 text-left text-white font-semibold">{p('label_reason')}</th>}
                  {isColVisible('final_settlement_amount') && (
                  <TableColumnHeaderFilter
                    columnKey="final_settlement_amount"
                    label={p('col_settlement')}
                    value={colFilters.final_settlement_amount ?? ''}
                    onChange={(v) => setColFilter('final_settlement_amount', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('status') && (
                  <TableColumnHeaderFilter columnKey="status" label={p('label_status')} kind="select" options={statusFilterOptions.filter((o) => o.value !== 'all')} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredDepartures.length > 0 ? (
                  filteredDepartures.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}><div className="flex justify-center"><QawaemTableCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} /></div></td>
                      
                      {isColVisible('departure_number') && (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{record.departure_number || p('na')}</span>
                      </td>
                      )}
                      {isColVisible('employee_name') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.employee_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('matricule_prefix')} {resolveHrEmployeeMatricule(realEmployees, record) || p('na')} | {p('notice_prefix')} {record.notice_date ? new Date(record.notice_date).toLocaleDateString(dateLocale) : p('na')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('departure_date') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Calendar size={14} className="text-white/50" />
                            {record.departure_date ? new Date(record.departure_date).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : p('empty_dash')}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.settlement_date ? `${p('settlement_prefix')} ${new Date(record.settlement_date).toLocaleDateString(dateLocale)}` : p('settlement_pending')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('departure_type') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getTypeBadge(record.departure_type)}</div></td>
                      )}
                      {isColVisible('reason') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold capitalize">{record.departure_reason || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.exit_interview_conducted ? p('interview_done_short') : p('interview_not_done_short')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('final_settlement_amount') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{formatAmountDisplay(record.final_settlement_amount || 0)}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.rehire_eligible ? p('rehire_possible') : p('not_rehireable')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('status') && (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} /></div></td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                          />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><UserMinus size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredDepartures.length > 0 ? (
              filteredDepartures.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2 min-w-0">
                          <UserMinus size={20} className="text-blue-400 shrink-0" />
                          <span className="truncate">{record.employee_name}</span>
                        </h3>
                        {getTypeBadge(record.departure_type)}
                        <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} />
                        {record.departure_date ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {new Date(record.departure_date).toLocaleDateString(dateLocale)}
                          </span>
                        ) : null}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_departure_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_number_prefix')}</span> {record.departure_number}</div>
                            <div><span className="font-medium">{p('label_matricule')}:</span> {resolveHrEmployeeMatricule(realEmployees, record) || p('na')}</div>
                            <div><span className="font-medium">{p('label_date')}:</span> {record.departure_date ? new Date(record.departure_date).toLocaleDateString(dateLocale) : '-'}</div>
                            <div><span className="font-medium">{p('label_notice_date')}:</span> {record.notice_date ? new Date(record.notice_date).toLocaleDateString(dateLocale) : '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_type_reason')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_type')}:</span> {record.departure_type}</div>
                            <div><span className="font-medium">{p('label_reason')}:</span> {record.departure_reason || '-'}</div>
                            <div><span className="font-medium">{p('label_interview')}:</span> {record.exit_interview_conducted ? p('yes') : p('no')}</div>
                            {record.exit_interview_notes && <div className="mt-2 p-2 bg-white/5 rounded"><p className="text-white/60 text-xs">{record.exit_interview_notes}</p></div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_final_settlement')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_amount')}:</span> {formatAmountDisplay(record.final_settlement_amount || 0)}</div>
                            <div><span className="font-medium">{p('label_date_short')}</span> {record.settlement_date ? new Date(record.settlement_date).toLocaleDateString(dateLocale) : '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_rehire')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_eligible')}:</span> {record.rehire_eligible ? p('yes') : p('no')}</div>
                            {record.notes && <div className="mt-2 p-2 bg-white/5 rounded"><span className="font-medium text-white/80 text-xs">{p('section_notes')}:</span><p className="text-white/60 text-xs mt-1">{record.notes}</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><UserMinus size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* View Modal */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingRecord(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => { setShowViewModal(false); setViewingRecord(null); }} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div >
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <UserMinus size={18} className="text-blue-400" />
                    {p('section_departure_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_departure_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.departure_number || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_employee')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.employee_name || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_matricule')}</p>
                      <p className="text-white font-medium">{resolveHrEmployeeMatricule(realEmployees, viewingRecord) || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_departure_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.departure_date ? new Date(viewingRecord.departure_date).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_notice_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.notice_date ? new Date(viewingRecord.notice_date).toLocaleDateString(dateLocale) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_type')}</p>
                      <div className="flex flex-wrap mt-1">{getTypeBadge(viewingRecord.departure_type)}</div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_status')}</p>
                      <div className="flex flex-wrap mt-1">
                        <QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title')} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <ClipboardList size={18} className="text-orange-400" />
                    {p('section_reason_details')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_reason')}</p>
                      <p className="text-white font-medium capitalize">{viewingRecord.departure_reason || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_exit_interview')}</p>
                      <p className="text-white font-medium">{viewingRecord.exit_interview_conducted ? p('interview_done') : p('interview_not_done')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('section_rehire')}</p>
                      <p className="text-white font-medium">{viewingRecord.rehire_eligible ? p('rehire_eligible') : p('rehire_not_eligible')}</p>
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-white/60 text-sm mb-1">{p('label_exit_interview_notes')}</p>
                      <p className="text-white font-medium">{viewingRecord.exit_interview_notes || p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <DollarSign size={18} className="text-green-400" />
                      {p('section_final_settlement')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_amount')}</p>
                        <p className="text-white font-medium text-lg">
                          {formatAmountDisplay(viewingRecord.final_settlement_amount || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_settlement_date')}</p>
                        <p className="text-white font-medium">
                          {viewingRecord.settlement_date ? new Date(viewingRecord.settlement_date).toLocaleDateString(dateLocale) : p('not_settled')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <StickyNote size={18} className="text-purple-400" />
                      {p('section_notes')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_remarks')}</p>
                        <p className="text-white font-medium">{viewingRecord.notes || p('none_notes')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_create')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="departure-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            
            
            <HrFormModalTabBar
              tabs={departureFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="departure-form" onSubmit={handleSubmit} >
                {formTab === 'departure' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_departure_number')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.departure_number}
                              onChange={(e) => setFormData({ ...formData, departure_number: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_departure_number')}
                            />
                            {!editingRecord && (
                              <p className="text-white/50 text-xs mt-1">{p('hint_auto_number')}</p>
                            )}
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_employee_name')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.employee_name}
                              onChange={(value) => {
                                const selectedEmployee = resolveHrEmployeeFromLabel(realEmployees, value);
                                if (selectedEmployee) {
                                  setFormData({
                                    ...formData,
                                    ...buildHrEmployeePickerPatch(selectedEmployee),
                                  });
                                } else {
                                  setFormData({ ...formData, employee_name: value, employee_id: '' });
                                }
                              }}
                              options={buildHrEmployeeSelectOptions(realEmployees)}
                              placeholder={p('placeholder_select_employee')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_employee_select')}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_departure_date')} <span className="qawaem-required-mark">*</span></label>
                            <CustomDatePicker
                              value={formData.departure_date}
                              onChange={(value) => setFormData({ ...formData, departure_date: value })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notice_date')}</label>
                            <CustomDatePicker
                              value={formData.notice_date}
                              onChange={(value) => setFormData({ ...formData, notice_date: value })}
                              placeholder={p('placeholder_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_departure_type')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.departure_type}
                              onChange={(value) => setFormData({ ...formData, departure_type: value })}
                              options={[
                                { value: SAUDI_DEPARTURE_TYPES.RESIGNATION, label: p('type_resignation') },
                                { value: SAUDI_DEPARTURE_TYPES.TERMINATION, label: p('type_termination') },
                                { value: SAUDI_DEPARTURE_TYPES.RETIREMENT, label: p('type_retirement') },
                                { value: SAUDI_DEPARTURE_TYPES.CONTRACT_END, label: p('type_contract_end') },
                                { value: SAUDI_DEPARTURE_TYPES.MUTUAL_AGREEMENT, label: p('type_mutual_agreement') },
                              ]}
                              placeholder={p('placeholder_select_type')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('hint_select_type')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status_required')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'en_cours', label: p('status_en_cours') },
                                { value: 'validé', label: p('status_valide') },
                                { value: 'annulé', label: p('status_annule') }
                              ]}
                              placeholder={p('placeholder_select_status')}
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'departure' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_departure_reason')}</label>
                          <CustomSelect
                            value={formData.departure_reason}
                            onChange={(value) => setFormData({ ...formData, departure_reason: value })}
                            options={[
                              { value: 'personnel', label: p('reason_personal') },
                              { value: 'performance', label: p('reason_performance') },
                              { value: 'redundancy', label: p('reason_redundancy') },
                              { value: 'misconduct', label: p('reason_misconduct') },
                              { value: 'health', label: p('reason_health') },
                              { value: 'end_contract', label: p('reason_end_contract') },
                              { value: 'other', label: p('reason_other') },
                            ]}
                            placeholder={p('placeholder_select_reason')}
                            searchable={true}
                            creatable={true}
                          />
                          <p className="text-white/50 text-xs mt-1">{p('hint_select_reason')}</p>
                        </div>
                        <div className="flex items-center gap-4 md:col-span-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={formData.exit_interview_conducted} onChange={(e) => setFormData({ ...formData, exit_interview_conducted: e.target.checked })} className="w-5 h-5 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500" />
                            <span className="text-white font-medium">{p('label_exit_interview_done')}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={formData.rehire_eligible} onChange={(e) => setFormData({ ...formData, rehire_eligible: e.target.checked })} className="w-5 h-5 rounded border-white/20 bg-white/10 text-green-500 focus:ring-2 focus:ring-green-500" />
                            <span className="text-white font-medium">{p('label_rehire_eligible')}</span>
                          </label>
                        </div>
                        <div className="md:col-span-3">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_exit_interview_notes')}</label>
                          <textarea value={formData.exit_interview_notes} onChange={(e) => setFormData({ ...formData, exit_interview_notes: e.target.value })} rows="3" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_exit_interview')} />
                        </div>
                    </div>
                  </>
                )}
                {formTab === 'settlement' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_basic_for_eosb')}</label>
                          <CustomNumberInput
                            value={formData.basicSalaryForEosb}
                            onChange={(value) => setFormData({ ...formData, basicSalaryForEosb: value })}
                            placeholder="8000"
                            min={0}
                          />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notice_period_days')}</label>
                          <CustomNumberInput
                            value={formData.noticePeriodDays}
                            onChange={(value) => setFormData({ ...formData, noticePeriodDays: value })}
                            placeholder="30"
                            min={0}
                          />
                          <p className="text-white/50 text-xs mt-1">{p('hint_notice_saudi')}</p>
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_eosb_amount')}</label>
                          <CustomNumberInput
                            value={formData.eosbAmount || formData.final_settlement_amount}
                            onChange={(value) => setFormData({ ...formData, eosbAmount: value, final_settlement_amount: value })}
                            placeholder="0.00"
                            step="0.01"
                          />
                          <button
                            type="button"
                            onClick={handleCalculateEosb}
                            className="mt-2 text-sm text-blue-300 hover:text-blue-200 underline"
                          >
                            {p('calculate_eosb')}
                          </button>
                          <p className="text-white/50 text-xs mt-1">{p('hint_eosb_calc')}</p>
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_settlement_date')}</label>
                          <CustomDatePicker
                            value={formData.settlement_date}
                            onChange={(value) => setFormData({ ...formData, settlement_date: value })}
                            placeholder={p('placeholder_date')}
                          />
                        </div>
                        <div className="md:col-span-2 flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={formData.exitVisaRequired} onChange={(e) => setFormData({ ...formData, exitVisaRequired: e.target.checked })} className="w-5 h-5 rounded" />
                            <span className="text-white text-sm">{p('label_exit_visa')}</span>
                          </label>
                          {formData.exitVisaRequired && (
                            <CustomSelect
                              value={formData.exitVisaStatus}
                              onChange={(value) => setFormData({ ...formData, exitVisaStatus: value })}
                              options={[
                                { value: 'pending', label: p('exit_visa_pending') },
                                { value: 'issued', label: p('exit_visa_issued') },
                                { value: 'completed', label: p('exit_visa_completed') },
                              ]}
                              placeholder={p('label_exit_visa_status')}
                            />
                          )}
                        </div>
                    </div>
                  </>
                )}
                {formTab === 'settlement' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="md:col-span-3">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="4" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_notes')} />
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default DepartsPage;

